/** Ctrl K / ⌘K command palette: documents (acronym-aware), recent items, jump-to actions. */
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../lib/store";
import { coName } from "../lib/brand";
import { PEND_LABEL, pendKey } from "../lib/access";
import { api, type DocView } from "../lib/api";
import { Co, DocNo, Hl, Tile, TypeBadge } from "./bits";
import { Icon, type IconName } from "./Icon";

type Row = { kind: "doc"; d: DocView; why: string } | { kind: "act"; label: string; sub: string; to: string; icon: IconName };

export function Palette({ onClose }: { onClose: () => void }) {
  const { p, ws, docs, recent, search, approvalsCount, terms, ai, me, tasksCount } = useStore();
  // Ask DocVault (approved mockup 2026-10-26): an Ask tab when AI is on for the company.
  const askCo = ws !== "ALL" ? (ai.on(ws, "ask") ? ws : null) : me.companies.map((c) => c.code).find((c) => ai.on(c, "ask")) ?? null;
  const [mode, setMode] = useState<"docs" | "ask">("docs");
  const [answer, setAnswer] = useState<Awaited<ReturnType<typeof api.aiAsk>> | null>(null);
  const [asking, setAsking] = useState(false);
  const [askErr, setAskErr] = useState<string | null>(null);
  const ask = async () => {
    if (!askCo || q.trim().length < 3) return;
    setAsking(true); setAskErr(null); setAnswer(null);
    try { setAnswer(await api.aiAsk(askCo, q.trim())); } catch (e) { setAskErr((e as Error).message); } finally { setAsking(false); }
  };
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const body = useRef<HTMLDivElement>(null);
  useEffect(() => { input.current?.focus(); }, []);

  const actions: Extract<Row, { kind: "act" }>[] = [
    { kind: "act", label: "New document", sub: "Start a controlled document", to: "/new", icon: "plus" as IconName, ok: p.anyAuthor },
    { kind: "act", label: "Approvals", sub: approvalsCount ? `${approvalsCount} waiting on you` : "Nothing waiting", to: "/approvals", icon: "ok" as IconName, ok: p.anyApprover },
    { kind: "act", label: "My tasks", sub: tasksCount ? `${tasksCount} waiting on you` : "Nothing waiting", to: "/tasks", icon: "tasks" as IconName, ok: true },
    { kind: "act", label: "Dashboard", sub: "Charts and your queue", to: "/", icon: "dash" as IconName, ok: true },
    { kind: "act", label: "Library", sub: "Every document you can see", to: "/library", icon: "lib" as IconName, ok: true },
    { kind: "act", label: "Search acronyms", sub: "IT, security and compliance terms", to: "/settings/acronyms", icon: "az" as IconName, ok: true },
  ].filter((a) => a.ok).map(({ ok: _ok, ...a }) => a as Extract<Row, { kind: "act" }>);

  const { sections, rows, expansions } = useMemo(() => {
    const list = docs ?? [];
    const sections: { title: string; start: number; end: number; extra?: "try" }[] = [];
    const rows: Row[] = [];
    const ql = q.trim().toLowerCase();
    let expansions: { term: string; expansion: string }[] = [];
    const push = (title: string, items: Row[], extra?: "try") => {
      sections.push({ title, start: rows.length, end: rows.length + items.length, extra });
      rows.push(...items);
    };
    if (!ql) {
      const rec = recent.map((id) => list.find((d) => d.id === id)).filter((d): d is DocView => !!d);
      if (rec.length) push("Recently viewed", rec.map((d) => ({ kind: "doc", d, why: "" })));
      push("Jump to", actions);
      push("Try an acronym", [], "try");
    } else {
      const res = search(q, list);
      expansions = res.expansions;
      push(`Documents · ${res.rows.length}`, res.rows.slice(0, 7).map((r) => ({ kind: "doc", d: r.d, why: r.why })));
      const am = actions.filter((a) => a.label.toLowerCase().includes(ql) || a.sub.toLowerCase().includes(ql));
      if (am.length) push("Actions", am);
    }
    return { sections, rows, expansions };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, docs, recent, search, approvalsCount]);

  useEffect(() => { body.current?.querySelector(".pal-item.on")?.scrollIntoView({ block: "nearest" }); }, [sel]);

  const open = (i: number) => {
    const r = rows[i];
    if (!r) return;
    onClose();
    nav(r.kind === "act" ? r.to : `/doc/${r.d.id}`);
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (mode === "ask") { if (e.key === "Enter") { e.preventDefault(); void ask(); } else if (e.key === "Escape") onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, rows.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); open(sel); }
    else if (e.key === "Escape") onClose();
  };

  const item = (r: Row, i: number): ReactNode => {
    if (r.kind === "act") {
      return (
        <div key={`a${i}`} className={`pal-item ${i === sel ? "on" : ""}`} onClick={() => open(i)} onMouseMove={() => setSel(i)}>
          <span className="act-ic"><Icon name={r.icon} /></span>
          <div className="t"><b>{r.label}</b><span>{r.sub}</span></div>
          <span className="go">↵ go</span>
        </div>
      );
    }
    const k = pendKey(r.d);
    return (
      <div key={r.d.id} className={`pal-item ${i === sel ? "on" : ""}`} onClick={() => open(i)} onMouseMove={() => setSel(i)}>
        <TypeBadge type={r.d.type} />
        <div className="t">
          <b><Hl text={r.d.title} q={q} expansions={expansions} /></b>
          <span><DocNo d={r.d} /><Co code={r.d.company} />{r.why && <span style={{ color: "var(--accent-ink)", fontWeight: 600 }}>{r.why}</span>}</span>
        </div>
        {k && p.canSeePending(r.d.company) ? <span className={`pill sm ${k}`}>{PEND_LABEL[k]}</span> : r.d.revision ? <span className="pill sm pub">Published</span> : null}
        <span className="go">↵ open</span>
      </div>
    );
  };

  const tryTerms = ["IR", "EDR", "BCP", "MFA", "CM", "PTO"].filter((t) => terms.some((x) => x.term === t) || t === "IR").concat(["SEC-001"]);

  return (
    <div className="scrim open" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="palette" role="dialog" aria-modal="true" aria-label="Search documents">
        <div className="pal-q">
          <span className="sic"><Icon name="search" size={18} /></span>
          <input ref={input} value={q} onChange={(e) => { setQ(e.target.value); setSel(0); }} onKeyDown={onKey}
            placeholder={mode === "ask" ? "Ask a question, then press Enter" : "Find a document, number or acronym…"} autoComplete="off" aria-label={mode === "ask" ? "Ask DocVault" : "Search"} />
          <span className="scope"><Tile code={ws} />{ws === "ALL" ? "All companies" : coName(ws)}</span>
          <span className="kbd">Esc</span>
        </div>
        {askCo && (
          <div className="seg ask-tabs" role="tablist" style={{ marginLeft: 14 }}>
            <button role="tab" aria-selected={mode === "docs"} className={mode === "docs" ? "on" : ""} onClick={() => { setMode("docs"); input.current?.focus(); }}>Documents</button>
            <button role="tab" aria-selected={mode === "ask"} className={mode === "ask" ? "on" : ""} onClick={() => { setMode("ask"); input.current?.focus(); }}>Ask</button>
          </div>
        )}
        {mode === "ask" ? (
          <div className="pal-body" style={{ padding: 14 }}>
            {asking ? <div className="cap">Reading your documents{askCo ? ` and ${coName(askCo)}'s client records` : ""}…</div>
              : askErr ? <div className="note bad" style={{ display: "block" }}>{askErr}</div>
              : answer ? (
                <div className="ask-ans"><span className="ai-dot">AI</span><div>
                  <div style={{ whiteSpace: "pre-wrap" }}>{answer.answer}</div>
                  {answer.sources.length > 0 && <div className="ask-src">{answer.sources.map((x) => <a key={x.n} className="doclink" onClick={() => { onClose(); nav(x.url); }}>[{x.n}] {x.title}{x.kind === "client" ? ", TechHub" : ""}</a>)}</div>}
                  <div className="cap">AI can be wrong. Check the sources before you act.</div></div></div>
              ) : <div className="cap">Answers come only from published documents and client records you can see, with numbered links to each source.</div>}
          </div>
        ) : (
        <div className="pal-body" ref={body}>
          {expansions.map((e) => (
            <div className="acr-card" key={e.term}>
              <span className="term">{e.term.toUpperCase()}</span>
              <div><span className="exp">{e.expansion}</span><small>{terms.find((t) => t.term.toLowerCase() === e.term.toLowerCase())?.category ?? "Acronym"} · searching for both</small></div>
            </div>
          ))}
          {sections.map((s) => (
            <div key={s.title}>
              <div className="pal-sec">{s.title}</div>
              {s.extra === "try" ? (
                <div className="try">
                  {tryTerms.map((t) => <button key={t} onClick={() => { setQ(t); setSel(0); input.current?.focus(); }}>{t}</button>)}
                </div>
              ) : s.end > s.start ? (
                rows.slice(s.start, s.end).map((r, j) => item(r, s.start + j))
              ) : (
                <div style={{ padding: "10px 12px", color: "var(--muted)", fontSize: 13 }}>
                  No documents match.{p.anyAdmin ? " Add the term in Search acronyms?" : ""}
                </div>
              )}
            </div>
          ))}
        </div>
        )}
        <div className="pfoot">
          <span><span className="kbd">↑</span> <span className="kbd">↓</span> move</span>
          <span><span className="kbd">Enter</span> open</span>
          <span><span className="kbd">Esc</span> close</span>
          <span style={{ marginLeft: "auto" }}>Understands acronyms, initials and doc numbers</span>
        </div>
      </div>
    </div>
  );
}
